if (!customElements.get('wlsn-product-recommendations')) {
  class WLSNProductRecommendations extends HTMLElement {
    observer = undefined;

    constructor() {
      super();

      this.classes = {
        loaded: 'wlsn-product-recommendations--loaded',
        complementary: 'complementary-products',
      };

      this.selectors = {
        recommendations: 'wlsn-product-recommendations',
        gridItem: '.grid__item',
        slider: 'wlsn-slider',
      };

      this.boundHandleIntersection = this._handleIntersection.bind(this);
    }

    /**
     * Get dataset attributes
     * @returns {object} - Configuration object with data attributes
     */
    get config() {
      return {
        productId: this.dataset.productId,
        sectionId: this.dataset.sectionId,
        url: this.dataset.url,
      };
    }

    /**
     * Connected callback lifecycle method
     * @returns {void}
     */
    connectedCallback() {
      this._initializeRecommendations();
    }

    /**
     * Disconnected callback lifecycle method
     * @returns {void}
     */
    disconnectedCallback() {
      if (this.observer) {
        this.observer.disconnect();
        this.observer = undefined;
      }
    }

    /**
     * Initialize IntersectionObserver to lazy load recommendations
     * @returns {void}
     */
    _initializeRecommendations() {
      // Clean up existing observer if present
      if (this.observer) {
        this.observer.unobserve(this);
      }

      // Create new IntersectionObserver with 400px bottom margin for preloading
      this.observer = new IntersectionObserver(this.boundHandleIntersection, { rootMargin: '0px 0px 400px 0px' });

      this.observer.observe(this);
    }

    /**
     * Handle intersection observer callback
     * @param {Array<IntersectionObserverEntry>} entries - Intersection observer entries
     * @param {IntersectionObserver} observer - The observer instance
     * @returns {void}
     */
    _handleIntersection(entries, observer) {
      const entry = entries[0];

      if (!entry.isIntersecting) return;

      // Stop observing once recommendations are loaded
      observer.unobserve(this);

      this._loadRecommendations();
    }

    /**
     * Fetch and render product recommendations from server
     * @returns {void}
     */
    _loadRecommendations() {
      const { productId, sectionId, url } = this.config;

      if (!productId || !sectionId || !url) {
        console.error('Product recommendations: Missing required data attributes');
        return;
      }

      const requestUrl = `${url}&product_id=${productId}&section_id=${sectionId}`;

      fetch(requestUrl)
        .then((response) => {
          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }
          return response.text();
        })
        .then((text) => {
          this._renderRecommendations(text);
        })
        .catch((error) => {
          console.error('Failed to load product recommendations:', error);
        });
    }

    /**
     * Parse and render recommendations HTML
     * @param {string} html - The HTML response from server
     * @returns {void}
     */
    _renderRecommendations(html) {
      const container = document.createElement('div');
      container.innerHTML = html;

      const recommendations = container.querySelector(this.selectors.recommendations);

      // Replace content if recommendations exist
      if (recommendations?.innerHTML.trim().length) {
        this.innerHTML = recommendations.innerHTML;
      }

      // Remove complementary products if no slider component
      if (!this.querySelector(this.selectors.slider) && this.classList.contains(this.classes.complementary)) {
        this.remove();
        return;
      }
    }
  }

  customElements.define('wlsn-product-recommendations', WLSNProductRecommendations);
}
